/**
 * Mojo Up AI Cloud → Help and support (docs/plans/2026-10-cloud-licences-and-operations.md,
 * section 3): the service's health as the public status page shows it, then the organisation's
 * requests to Mojo Up (help, a bug, a billing question) with Mojo Up's answers, held by the Mojo Up
 * customer portal. Anyone in the organisation may ask; owners and admins see every request, a
 * member their own. `/help?ticket=<id>` opens one request (the link in Mojo Up's email).
 */

import { SUPPORT_KIND_WORDS, SUPPORT_STATUS_WORDS, type StatusState, type SupportKind, type SupportStatus, type SupportTicket } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { ArrowUpRightFromSquare, CircleQuestion, Comment, HeartPulse, Megaphone, PaperPlane } from '@gravity-ui/icons';
import { Button, Input, Label, TextArea, TextField } from '@heroui/react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { problemText, statusRoutes, supportRoutes } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow, Page, PageHeader, Stat, StatusMark } from '../../components/ui.tsx';
import { ArtEmpty, type ArtTone } from '../../components/ui/Art.tsx';
import { PartBoundary } from '../../components/ui/PartBoundary.tsx';
import { FormModal } from '../../components/cloud/FormModal.tsx';
import { timeAgo, useLoad } from '../../hooks.ts';

const tone: ArtTone = 'teal';
const SUPPORT_EMAIL = 'support@mojoup.com.au';

const HEALTH: Record<StatusState, { state: 'online' | 'medium' | 'error'; label: string; short: string }> = {
  ok: { state: 'online', label: 'All systems working', short: 'Working' },
  degraded: { state: 'medium', label: 'Some systems degraded', short: 'Degraded' },
  down: { state: 'error', label: 'Major outage', short: 'Outage' },
};
const STATUS_MARK: Record<SupportStatus, 'online' | 'working' | 'medium' | 'connected' | 'disabled'> = { open: 'online', in_progress: 'working', waiting: 'medium', resolved: 'connected', closed: 'disabled' };
const when = (iso: string) => dateTime(iso, { date: 'medium', year: false });
/** Mojo Up wrote last and the request is still open: the portal marks it `waiting` until the requester replies. */
const waitingOnYou = (t: SupportTicket): boolean => t.status === 'waiting';

export function HelpPage() {
  const cloud = useCloud()!;
  const org = cloud.organisation;
  const [params, setParams] = useSearchParams();
  const summary = useLoad(() => statusRoutes.summary(), [], 60_000);
  const list = useLoad(() => supportRoutes.list(org.id), [org.id], 60_000);
  const [asking, setAsking] = useState(false);
  const openId = params.get('ticket');
  // `?organisation=<id>` from Mojo Up's email: switch to that organisation first.
  const wanted = params.get('organisation');
  useEffect(() => {
    if (!wanted) return;
    if (wanted !== org.id && cloud.me.organisations.some((o) => o.id === wanted)) cloud.switchTo(wanted);
    const next = new URLSearchParams(params);
    next.delete('organisation');
    setParams(next, { replace: true });
  }, [wanted, org.id, cloud, params, setParams]);
  const open = (id?: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('ticket', id);
    else next.delete('ticket');
    setParams(next);
  };

  const s = summary.data;
  const health = s ? HEALTH[s.overall] : undefined;
  const tickets = list.data?.tickets ?? [];
  const waiting = tickets.filter(waitingOnYou);
  const configured = list.data?.configured ?? true;

  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<CircleQuestion />}
        title="Help and support"
        subtitle={`How AI Cloud is running, and ${org.name}'s support requests.`}
        actions={
          <>
            <Button size="sm" variant="secondary" onPress={() => window.open(statusRoutes.pageUrl, '_blank', 'noopener')}>
              <ArrowUpRightFromSquare className="size-4" />
              Status page
            </Button>
            {configured ? (
              <Button size="sm" variant="primary" onPress={() => setAsking(true)}>
                Ask for help
              </Button>
            ) : (
              <Button size="sm" variant="primary" onPress={() => window.location.assign(`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Mojo Up AI Cloud: ${org.name}`)}`)}>
                Email support
              </Button>
            )}
          </>
        }
      />
      <PartBoundary key="stats" name="Overview" tone={tone}>
        <div className="art-stats">
          <Stat tone={tone} icon={<HeartPulse />} label="Service" value={health?.short} detail={s ? (s.incidents.open.length ? `${s.incidents.open.length} open ${s.incidents.open.length === 1 ? 'incident' : 'incidents'}` : s.overall === 'ok' ? (s.checkedAt ? `All working, checked ${timeAgo(s.checkedAt)}` : 'All working') : (health?.label ?? '')) : ''} needsYou={!!s && s.overall !== 'ok'} />
          <Stat tone={tone} icon={<Comment />} label="Your requests" value={list.data ? tickets.filter((t) => t.status !== 'closed' && t.status !== 'resolved').length : undefined} detail={list.data ? (configured ? `${tickets.length} in all` : 'By email for now') : ''} />
          <Stat tone={tone} icon={<Megaphone />} label="Waiting for you" value={list.data ? waiting.length : undefined} detail={waiting.length ? 'Support has answered' : 'Nothing waits on you'} needsYou={waiting.length > 0} onPress={waiting[0] ? () => open(waiting[0]!.id) : undefined} />
        </div>
        <ErrorNote error={summary.error ?? list.error} />
      </PartBoundary>
      <PartBoundary key="health" name="Service health" tone={tone}>
        {s && (s.overall !== 'ok' || s.incidents.open.length) ? (
          <ListGroup title="Service health" description="What the public status page shows now.">
            {s.incidents.open.map((i) => (
              <ListRow key={i.id} icon={<HeartPulse />} title={i.title} description={`Since ${when(i.startedAt)}${i.updates.length ? ` · ${i.updates[i.updates.length - 1]!.text}` : ''}`} status={<StatusMark state="medium" label="Open incident" />} />
            ))}
            {s.components
              .filter((c) => c.state !== 'ok')
              .map((c) => (
                <ListRow key={c.id} icon={<HeartPulse />} title={c.name} description={c.message} status={<StatusMark state={HEALTH[c.state].state} label={c.state === 'degraded' ? 'Degraded' : 'Down'} />} />
              ))}
          </ListGroup>
        ) : null}
      </PartBoundary>
      <PartBoundary key="requests" name="Requests" tone={tone}>
        <ListGroup
          title="Support requests"
          count={list.data ? tickets.length || undefined : undefined}
          description={org.role === 'member' || org.role === 'viewer' ? 'Yours; the owners and admins see everyone’s.' : "Everyone's in this organisation."}
          empty={
            !configured ? (
              <ArtEmpty tone={tone} icon={<Comment />} title="Email support" compact>
                Requests are not connected to support here yet. Write to {SUPPORT_EMAIL} and name your organisation, {org.name}.
              </ArtEmpty>
            ) : list.data && tickets.length === 0 ? (
              <ArtEmpty
                tone={tone}
                icon={<Comment />}
                title="No requests yet"
                compact
                action={
                  <Button size="sm" variant="secondary" onPress={() => setAsking(true)}>
                    Ask for help
                  </Button>
                }
              >
                Help with something, a bug, or a billing question: we answer here and by email.
              </ArtEmpty>
            ) : undefined
          }
        >
          {!list.data && !list.error ? <ListBlock className="text-sm text-muted">Reading your requests…</ListBlock> : null}
          {tickets.map((t) => (
            <ListRow
              key={t.id}
              onPress={() => open(t.id)}
              icon={<Comment />}
              title={t.subject}
              description={`${t.reference ? `${t.reference} · ` : ''}${SUPPORT_KIND_WORDS[t.kind]} · ${t.requester.name ?? t.requester.email} · ${timeAgo(t.updatedAt)}`}
              status={<StatusMark state={waitingOnYou(t) ? 'medium' : STATUS_MARK[t.status]} label={waitingOnYou(t) ? 'Support answered' : SUPPORT_STATUS_WORDS[t.status]} />}
            />
          ))}
        </ListGroup>
      </PartBoundary>
      {asking ? (
        <AskModal
          organisationId={org.id}
          onClose={() => setAsking(false)}
          onSent={(t) => {
            list.reload();
            open(t.id);
          }}
        />
      ) : null}
      {openId ? <ThreadModal organisationId={org.id} ticketId={openId} onClose={() => open(undefined)} onChanged={() => list.reload()} /> : null}
    </Page>
  );
}

function AskModal({ organisationId, onClose, onSent }: { organisationId: string; onClose: () => void; onSent: (t: SupportTicket) => void }) {
  const [kind, setKind] = useState<SupportKind>('support');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const t = await supportRoutes.create(organisationId, { kind, subject, message });
      onSent(t);
      onClose();
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  return (
    <FormModal isOpen onClose={onClose} title="Ask for help" blurb="We answer here and by email, to the address you sign in with." submitLabel="Send" canSubmit={subject.trim().length >= 3 && message.trim().length >= 10} busy={busy} error={error} onSubmit={() => void send()}>
      <ChoiceSelect<SupportKind> label="What it is about" value={kind} options={SUPPORT_KIND_WORDS} onChange={setKind} />
      <TextField variant="secondary" value={subject} onChange={setSubject} isRequired autoFocus>
        <Label>Subject</Label>
        <Input placeholder="In a few words" maxLength={200} />
      </TextField>
      <TextField variant="secondary" value={message} onChange={setMessage} isRequired>
        <Label>What happened, or what you need</Label>
        <TextArea rows={5} placeholder="What you did, what you expected, and what you saw. For a bug, which workbench or node and when." />
      </TextField>
    </FormModal>
  );
}

function ThreadModal({ organisationId, ticketId, onClose, onChanged }: { organisationId: string; ticketId: string; onClose: () => void; onChanged: () => void }) {
  const ticket = useLoad(() => supportRoutes.get(organisationId, ticketId), [organisationId, ticketId]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const t = ticket.data;
  const act = async (what: 'reply' | 'close') => {
    setBusy(true);
    setError(undefined);
    try {
      if (what === 'reply') await supportRoutes.reply(organisationId, ticketId, text);
      else await supportRoutes.close(organisationId, ticketId);
      setText('');
      ticket.reload();
      onChanged();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const closed = t?.status === 'closed';
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={t?.subject ?? 'Request'}
      blurb={t ? `${t.reference ? `${t.reference} · ` : ''}${SUPPORT_KIND_WORDS[t.kind]} · ${SUPPORT_STATUS_WORDS[t.status]}` : ticket.error ? undefined : 'Loading…'}
      submitLabel="Send reply"
      canSubmit={!closed && text.trim().length > 0}
      busy={busy}
      error={error ?? ticket.error}
      onSubmit={() => void act('reply')}
      footer={
        <>
          {t && !closed ? (
            <Button variant="tertiary" onPress={() => void act('close')} isDisabled={busy}>
              Close the request
            </Button>
          ) : null}
          <Button variant="secondary" onPress={onClose}>
            Done
          </Button>
          {t && !closed ? (
            <Button variant="primary" onPress={() => void act('reply')} isDisabled={busy || text.trim().length === 0}>
              <PaperPlane className="size-4" />
              Send reply
            </Button>
          ) : null}
        </>
      }
    >
      <ol className="grid max-h-80 gap-3 overflow-y-auto">
        {(t?.messages ?? []).map((m) => (
          <li key={m.id} className={`rounded-xl px-3 py-2 text-sm ${m.from === 'mojoup' ? 'bg-accent/10' : 'bg-default/40'}`}>
            <span className="block text-xs text-muted">
              {m.from === 'mojoup' ? (m.authorName ?? 'AI Cloud support') : (m.authorName ?? 'You')} · {when(m.at)}
            </span>
            <span className="whitespace-pre-wrap">{m.text}</span>
          </li>
        ))}
      </ol>
      {t && !closed ? (
        <TextField variant="secondary" value={text} onChange={setText}>
          <Label>Reply</Label>
          <TextArea rows={3} placeholder="Write to support" />
        </TextField>
      ) : t ? (
        <p className="text-sm text-muted">This request is closed. Ask again if it comes back.</p>
      ) : null}
    </FormModal>
  );
}
